import { useQuery } from '@tanstack/react-query';
import type { CitizenRow, ConversationView, Page } from '@vmc/shared';
import { Command } from 'cmdk';
import {
  BookOpenCheck,
  Clock,
  Megaphone,
  MessagesSquare,
  Moon,
  Plus,
  Rows3,
  Sun,
  Upload,
  User,
  type LucideIcon,
} from 'lucide-react';
import { Dialog } from 'radix-ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { fmtPhone } from '@/lib/format';
import { useDebouncedValue } from '@/lib/hooks';
import { useHotkey } from '@/lib/hotkeys';
import { usePref, useRecents, type Recent } from '@/lib/prefs';
import { useTheme } from '@/lib/theme';
import { NAV_SECTIONS } from './nav';

/**
 * Ctrl/⌘+K (DESIGN.md rule 3): go anywhere, find a citizen by name or phone, jump to a chat, or
 * run a common action, without touching the mouse.
 */
function recentPath({ kind, id }: Recent): string {
  if (kind === 'chat') return `/chats/${id}`;
  if (kind === 'kb') return `/kb/${id}`;
  return `/citizens?citizen=${id}`;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const can = useCan();
  const [recents, addRecent] = useRecents();
  const { theme, setTheme } = useTheme();
  const [density, setDensity] = usePref('density');
  const [selection, setSelection] = useState('');
  const q = useDebouncedValue(query.trim());

  useHotkey('mod+k', () => setOpen((o) => !o), {
    allowInInputs: true,
    description: 'Command palette',
    group: 'General',
  });

  const citizens = useQuery({
    queryKey: ['palette', 'citizens', q],
    queryFn: ({ signal }) =>
      api.get<Page<CitizenRow>>('/api/citizens', { search: q, limit: 6 }, signal),
    enabled: open && q.length >= 2 && can('citizens:read'),
    staleTime: 10_000,
  });
  const chats = useQuery({
    queryKey: ['palette', 'chats'],
    queryFn: ({ signal }) =>
      api.get<Page<ConversationView>>('/api/conversations', { limit: 50 }, signal),
    enabled: open && can('chats:handle'),
    staleTime: 10_000,
  });

  const go = (to: string) => {
    setOpen(false);
    setQuery('');
    void navigate(to);
  };
  const run = (fn: () => void) => {
    setOpen(false);
    setQuery('');
    fn();
  };

  const openCitizen = (c: CitizenRow) => {
    addRecent({
      kind: 'citizen',
      id: c.id,
      label: c.name ?? fmtPhone(c.waId),
      sub: fmtPhone(c.waId),
    });
    go(`/citizens?citizen=${c.id}`);
  };
  const openChat = (c: ConversationView) => {
    addRecent({
      kind: 'chat',
      id: c.id,
      label: c.citizen.name ?? fmtPhone(c.citizen.waId),
      sub: 'Chat',
    });
    go(`/chats/${c.id}`);
  };

  const lower = q.toLowerCase();
  const digits = q.replace(/\D/g, '');
  const hit = (label: string) => !q || label.toLowerCase().includes(lower);

  // Sections as data: empty ones disappear, and the selection can be derived below.
  const sections: Section[] = [
    {
      heading: 'Recent',
      items: q
        ? []
        : recents.map((r) => ({
            value: `recent:${r.kind}:${r.id}`,
            icon: Clock,
            label: r.label,
            hint: r.sub,
            onSelect: () => go(recentPath(r)),
          })),
    },
    {
      heading: 'Citizens',
      items: (citizens.data?.items ?? []).map((c) => ({
        value: `citizen:${c.id}`,
        icon: User,
        label: c.name ?? fmtPhone(c.waId),
        detail: c.ward ? `Ward ${c.ward.number}` : undefined,
        hint: fmtPhone(c.waId),
        onSelect: () => openCitizen(c),
      })),
    },
    {
      heading: 'Open chats',
      items: (chats.data?.items ?? [])
        .filter(
          (c) =>
            !q ||
            (c.citizen.name ?? '').toLowerCase().includes(lower) ||
            (digits.length >= 3 && c.citizen.waId.includes(digits)),
        )
        .slice(0, q ? 6 : 4)
        .map((c) => ({
          value: `chat:${c.id}`,
          icon: MessagesSquare,
          label: c.citizen.name ?? fmtPhone(c.citizen.waId),
          hint: c.status === 'QUEUED' ? 'Waiting' : c.assignedAgent?.name,
          onSelect: () => openChat(c),
        })),
    },
    {
      heading: 'Go to',
      items: NAV_SECTIONS.flatMap((sec) => sec.items)
        .filter((i) => can(i.permission) && hit(i.label))
        .map((i) => ({
          value: `page:${i.to}`,
          icon: i.icon,
          label: i.label,
          onSelect: () => go(i.to),
        })),
    },
    {
      heading: 'Actions',
      items: [
        ...ACTIONS.filter((a) => can(a.permission) && hit(a.label)).map((a) => ({
          value: `action:${a.to}`,
          icon: a.icon,
          label: a.label,
          onSelect: () => go(a.to),
        })),
        ...(hit('theme dark light')
          ? [
              {
                value: 'action:theme',
                icon: theme === 'dark' ? Sun : Moon,
                label: `Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`,
                onSelect: () => run(() => setTheme(theme === 'dark' ? 'light' : 'dark')),
              },
            ]
          : []),
        ...(hit('density compact comfortable table rows')
          ? [
              {
                value: 'action:density',
                icon: Rows3,
                label: `${density === 'compact' ? 'Comfortable' : 'Compact'} table rows`,
                onSelect: () =>
                  run(() => setDensity(density === 'compact' ? 'comfortable' : 'compact')),
              },
            ]
          : []),
      ],
    },
  ].filter((sec) => sec.items.length > 0);

  // Results arrive asynchronously: keep the highlighted item if it's still listed, else the first.
  const values = sections.flatMap((sec) => sec.items.map((i) => i.value));
  const selected = values.includes(selection) ? selection : (values[0] ?? '');

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-overlay" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed top-24 left-1/2 z-50 w-full max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border bg-popover outline-none text-popover-foreground shadow-2xl"
        >
          <Dialog.Title className="sr-only">Command palette</Dialog.Title>
          <Command
            label="Command palette"
            shouldFilter={false}
            loop
            value={selected}
            onValueChange={setSelection}
          >
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Search pages, citizens, chats… or type a command"
              className="h-12 w-full border-b bg-transparent px-4 text-sm outline-none placeholder:text-muted-foreground"
            />
            <Command.List className="max-h-96 overflow-y-auto p-1.5">
              {sections.length === 0 && (
                <div className="px-3 py-8 text-center text-ui text-muted-foreground">
                  {citizens.isFetching ? 'Searching…' : 'No matches'}
                </div>
              )}
              {sections.map((sec) => (
                <Command.Group
                  key={sec.heading}
                  heading={sec.heading}
                  className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:uppercase"
                >
                  {sec.items.map((item) => (
                    <PaletteItem key={item.value} item={item} />
                  ))}
                </Command.Group>
              ))}
            </Command.List>
            <div className="flex items-center gap-3 border-t px-3 py-2 text-2xs text-muted-foreground">
              <span>
                <kbd>↑</kbd> <kbd>↓</kbd> move
              </span>
              <span>
                <kbd>Enter</kbd> open
              </span>
              <span>
                <kbd>Esc</kbd> close
              </span>
              <span className="ml-auto">
                <kbd>?</kbd> all shortcuts
              </span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface PaletteEntry {
  value: string;
  icon: LucideIcon;
  label: string;
  detail?: string;
  hint?: string;
  onSelect: () => void;
}
interface Section {
  heading: string;
  items: PaletteEntry[];
}

function PaletteItem({ item }: { item: PaletteEntry }) {
  const Icon = item.icon;
  return (
    <Command.Item
      value={item.value}
      onSelect={item.onSelect}
      className="flex h-9 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-ui data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
    >
      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1 truncate">
        {item.label}
        {item.detail && <span className="text-muted-foreground"> · {item.detail}</span>}
      </span>
      {item.hint && <span className="shrink-0 text-2xs text-muted-foreground">{item.hint}</span>}
    </Command.Item>
  );
}

const ACTIONS: {
  label: string;
  to: string;
  icon: LucideIcon;
  permission: Parameters<ReturnType<typeof useCan>>[0];
}[] = [
  { label: 'New broadcast', to: '/broadcasts/new', icon: Megaphone, permission: 'broadcasts:send' },
  {
    label: 'Import citizens',
    to: '/citizens?dialog=import',
    icon: Upload,
    permission: 'citizens:import',
  },
  { label: 'Add citizen', to: '/citizens?dialog=add', icon: Plus, permission: 'citizens:write' },
  { label: 'New knowledge-base entry', to: '/kb/new', icon: Plus, permission: 'kb:write' },
  { label: 'Test the bot', to: '/kb/test', icon: BookOpenCheck, permission: 'kb:read' },
  { label: 'New template', to: '/templates/new', icon: Plus, permission: 'templates:write' },
  { label: 'Add admin user', to: '/users?dialog=new', icon: Plus, permission: 'users:manage' },
];
